import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDocs, 
  query, 
  orderBy, 
  where,
  onSnapshot,
  Timestamp,
  writeBatch
} from 'firebase/firestore';
import { db } from '../config/firebase';

export interface Trade {
  id: string;
  broker: string;
  symbol: string;
  type: 'buy' | 'sell';
  entryPrice: number;
  exitPrice?: number;
  quantity: number;
  entryTime: Date;
  exitTime?: Date;
  stopLoss?: number;
  takeProfit?: number;
  commission: number;
  swap?: number;
  pnl?: number;
  status: 'open' | 'closed' | 'cancelled';
  strategy?: string;
  notes?: string;
  tags?: string[];
  confidence?: number; // 1-5 scale
  emotion?: 'confident' | 'fearful' | 'greedy' | 'neutral' | 'disciplined';
  marketCondition?: 'trending' | 'ranging' | 'volatile' | 'calm';
  timeframe?: string;
  setup?: string;
  screenshots?: string[];
  createdAt: Date;
  updatedAt: Date;
}

export interface TradingStatistics {
  totalTrades: number;
  winningTrades: number;
  losingTrades: number;
  winRate: number;
  totalPnL: number;
  averageWin: number;
  averageLoss: number;
  profitFactor: number;
  averageRR: number;
  maxDrawdown: number;
  consecutiveWins: number;
  consecutiveLosses: number;
  largestWin: number;
  largestLoss: number;
  totalCommission: number;
  netProfit: number;
}

interface TradingJournalContextType {
  trades: Trade[];
  loading: boolean;
  error: string | null;
  addTrade: (trade: Omit<Trade, 'id' | 'createdAt' | 'updatedAt'>) => Promise<void>;
  updateTrade: (id: string, updates: Partial<Trade>) => Promise<void>;
  deleteTrade: (id: string) => Promise<void>;
  deleteTrades: (ids: string[]) => Promise<void>;
  getTradesByBroker: (broker: string) => Trade[];
  getTradesByStatus: (status: string) => Trade[];
  getTradesBySymbol: (symbol: string) => Trade[];
  getTradeStatistics: () => TradingStatistics;
  clearError: () => void;
}

const TradingJournalContext = createContext<TradingJournalContextType | undefined>(undefined);

interface TradingJournalProviderProps {
  children: ReactNode;
  userId?: string;
}

export const TradingJournalProvider: React.FC<TradingJournalProviderProps> = ({ 
  children, 
  userId = 'demo_user' 
}) => {
  const [trades, setTrades] = useState<Trade[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load trades from Firestore
  useEffect(() => {
    if (!userId) return;

    const tradesRef = collection(db, 'users', userId, 'trades');
    const q = query(tradesRef, orderBy('createdAt', 'desc'));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const tradesData: Trade[] = [];
        snapshot.forEach((doc) => {
          const data = doc.data();
          tradesData.push({
            id: doc.id,
            ...data,
            entryTime: data.entryTime?.toDate() || new Date(),
            exitTime: data.exitTime?.toDate() || null,
            createdAt: data.createdAt?.toDate() || new Date(),
            updatedAt: data.updatedAt?.toDate() || new Date(),
          } as Trade);
        });
        setTrades(tradesData);
        setLoading(false);
      },
      (err) => {
        console.error('Error loading trades:', err);
        setError('Failed to load trades');
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [userId]);

  const addTrade = async (tradeData: Omit<Trade, 'id' | 'createdAt' | 'updatedAt'>) => {
    try {
      setError(null);
      const tradesRef = collection(db, 'users', userId, 'trades');
      
      // Use provided P&L value; default to 0 if not supplied
      const pnl = tradeData.pnl ?? 0;

      const newTrade: any = {
        ...tradeData,
        pnl,
        entryTime: Timestamp.fromDate(tradeData.entryTime),
        exitTime: tradeData.exitTime ? Timestamp.fromDate(tradeData.exitTime) : null,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      };

      // Remove undefined properties so Firestore does not receive them
      Object.keys(newTrade).forEach((key) => {
        if (newTrade[key] === undefined) {
          delete newTrade[key];
        }
      });

      await addDoc(tradesRef, newTrade);
    } catch (err) {
      console.error('Error adding trade:', err);
      setError('Failed to add trade');
      throw err;
    }
  };

  const updateTrade = async (id: string, updates: Partial<Trade>) => {
    try {
      setError(null);
      const tradeRef = doc(db, 'users', userId, 'trades', id);

      // Do not recalculate PnL automatically; rely on user-provided updates

      const updateData = {
        ...updates,
        exitTime: updates.exitTime ? Timestamp.fromDate(updates.exitTime) : undefined,
        updatedAt: Timestamp.now(),
      };

      // Remove undefined values
      Object.keys(updateData).forEach(key => {
        if (updateData[key as keyof typeof updateData] === undefined) {
          delete updateData[key as keyof typeof updateData];
        }
      });

      await updateDoc(tradeRef, updateData);
    } catch (err) {
      console.error('Error updating trade:', err);
      setError('Failed to update trade');
      throw err;
    }
  };

  const deleteTrade = async (id: string) => {
    try {
      setError(null);
      const tradeRef = doc(db, 'users', userId, 'trades', id);
      await deleteDoc(tradeRef);
    } catch (err) {
      console.error('Error deleting trade:', err);
      setError('Failed to delete trade');
      throw err;
    }
  };

  const deleteTrades = async (ids: string[]) => {
    try {
      setError(null);
      const batch = writeBatch(db);
      
      ids.forEach(id => {
        const tradeRef = doc(db, 'users', userId, 'trades', id);
        batch.delete(tradeRef);
      });
      
      await batch.commit();
    } catch (err) {
      console.error('Error deleting trades:', err);
      setError('Failed to delete trades');
      throw err;
    }
  };

  const getTradesByBroker = (broker: string): Trade[] => {
    return trades.filter(trade => trade.broker === broker);
  };

  const getTradesByStatus = (status: string): Trade[] => {
    return trades.filter(trade => trade.status === status);
  };

  const getTradesBySymbol = (symbol: string): Trade[] => {
    return trades.filter(trade => trade.symbol === symbol);
  };

  const getTradeStatistics = (): TradingStatistics => {
    const closedTrades = trades.filter(trade => trade.status === 'closed' && trade.pnl !== undefined);
    const totalTrades = closedTrades.length;
    
    if (totalTrades === 0) {
      return {
        totalTrades: 0,
        winningTrades: 0,
        losingTrades: 0,
        winRate: 0,
        totalPnL: 0,
        averageWin: 0,
        averageLoss: 0,
        profitFactor: 0,
        averageRR: 0,
        maxDrawdown: 0,
        consecutiveWins: 0,
        consecutiveLosses: 0,
        largestWin: 0,
        largestLoss: 0,
        totalCommission: 0,
        netProfit: 0,
      };
    }

    const winningTrades = closedTrades.filter(trade => (trade.pnl || 0) > 0);
    const losingTrades = closedTrades.filter(trade => (trade.pnl || 0) < 0);
    
    const totalPnL = closedTrades.reduce((sum, trade) => sum + (trade.pnl || 0), 0);
    const totalCommission = trades.reduce((sum, trade) => sum + trade.commission, 0);
    const totalSwap = trades.reduce((sum, trade) => sum + (trade.swap || 0), 0);
    
    const winningPnL = winningTrades.reduce((sum, trade) => sum + (trade.pnl || 0), 0);
    const losingPnL = Math.abs(losingTrades.reduce((sum, trade) => sum + (trade.pnl || 0), 0));
    
    const averageWin = winningTrades.length > 0 ? winningPnL / winningTrades.length : 0;
    const averageLoss = losingTrades.length > 0 ? losingPnL / losingTrades.length : 0;
    
    const profitFactor = losingPnL > 0 ? winningPnL / losingPnL : winningPnL > 0 ? Infinity : 0;
    const winRate = (winningTrades.length / totalTrades) * 100;
    
    // Calculate average risk/reward ratio
    const rrRatios = closedTrades.map(trade => {
      if (!trade.stopLoss || !trade.takeProfit) return 0;
      const risk = Math.abs(trade.entryPrice - trade.stopLoss);
      const reward = Math.abs(trade.takeProfit - trade.entryPrice);
      return risk > 0 ? reward / risk : 0;
    }).filter(rr => rr > 0);
    
    const averageRR = rrRatios.length > 0 ? rrRatios.reduce((sum, rr) => sum + rr, 0) / rrRatios.length : 0;
    
    // Calculate max drawdown
    let runningBalance = 0;
    let peak = 0;
    let maxDrawdown = 0;
    
    closedTrades.forEach(trade => {
      runningBalance += (trade.pnl || 0);
      if (runningBalance > peak) {
        peak = runningBalance;
      }
      const drawdown = peak - runningBalance;
      if (drawdown > maxDrawdown) {
        maxDrawdown = drawdown;
      }
    });
    
    // Calculate consecutive wins/losses
    let consecutiveWins = 0;
    let consecutiveLosses = 0;
    let currentWinStreak = 0;
    let currentLossStreak = 0;
    
    closedTrades.forEach(trade => {
      if ((trade.pnl || 0) > 0) {
        currentWinStreak++;
        currentLossStreak = 0;
        consecutiveWins = Math.max(consecutiveWins, currentWinStreak);
      } else {
        currentLossStreak++;
        currentWinStreak = 0;
        consecutiveLosses = Math.max(consecutiveLosses, currentLossStreak);
      }
    });
    
    const largestWin = winningTrades.length > 0 ? Math.max(...winningTrades.map(t => t.pnl || 0)) : 0;
    const largestLoss = losingTrades.length > 0 ? Math.min(...losingTrades.map(t => t.pnl || 0)) : 0;
    
    return {
      totalTrades,
      winningTrades: winningTrades.length,
      losingTrades: losingTrades.length,
      winRate,
      totalPnL,
      averageWin,
      averageLoss,
      profitFactor,
      averageRR,
      maxDrawdown,
      consecutiveWins,
      consecutiveLosses,
      largestWin,
      largestLoss,
      totalCommission,
      netProfit: totalPnL - totalCommission - totalSwap,
    };
  };

  const clearError = () => {
    setError(null);
  };

  const value: TradingJournalContextType = {
    trades,
    loading,
    error,
    addTrade,
    updateTrade,
    deleteTrade,
    deleteTrades,
    getTradesByBroker,
    getTradesByStatus,
    getTradesBySymbol,
    getTradeStatistics,
    clearError,
  };

  return (
    <TradingJournalContext.Provider value={value}>
      {children}
    </TradingJournalContext.Provider>
  );
};

export const useTradingJournal = (): TradingJournalContextType => {
  const context = useContext(TradingJournalContext);
  if (context === undefined) {
    throw new Error('useTradingJournal must be used within a TradingJournalProvider');
  }
  return context;
}; 